/**
 * Remembers the last account used, so quick-add opens pre-filled.
 *
 * localStorage rather than server state: it is a UI convenience, it is
 * per-device (the phone and the laptop are used for different things), and it
 * must be available synchronously on first render or the field would flicker.
 */
import { useCallback, useState } from 'react';
import type { AccountRef } from '@/lib/types';

const KEY = 'mk-last-account';

function read(): Partial<AccountRef> | undefined {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<AccountRef>;
    // Guard against a stale shape from an older version.
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

export function useLastUsed(): {
  lastAccount: Partial<AccountRef> | undefined;
  remember: (ref: AccountRef) => void;
} {
  const [lastAccount, setLastAccount] = useState<Partial<AccountRef> | undefined>(read);

  const remember = useCallback((ref: AccountRef) => {
    setLastAccount(ref);
    try {
      localStorage.setItem(KEY, JSON.stringify(ref));
    } catch {
      /* private browsing — the convenience is simply lost */
    }
  }, []);

  return { lastAccount, remember };
}
